import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { ContentStyles } from './contentstyles';

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021];
const DESKTOP_PAGE_SIZE = 10;
const MOBILE_PAGE_SIZE = 5;

const normalise = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim();

const statusLabel = (value) => {
  if (!value || value === '-')
    return 'Occupation did not exist before ANZSCO 2022';
  if (value === 'No Shortage') return 'No shortage';
  return value;
};

const statusCode = (value) => {
  if (!value || value === '-') return '—';
  if (value === 'No Shortage') return 'NS';
  if (value === 'Shortage') return 'S';
  if (value === 'Regional Shortage') return 'R';
  if (value === 'Metropolitan Shortage') return 'M';
  return value;
};

const statusClass = (value) => {
  if (value === 'No Shortage') return 'rating-code--no-shortage';
  if (value === 'Shortage') return 'rating-code--shortage';
  if (value === 'Regional Shortage') return 'rating-code--regional';
  if (value === 'Metropolitan Shortage') return 'rating-code--metropolitan';
  return 'rating-code--unknown';
};

function Rating({ year, value }) {
  return (
    <span
      className={`rating-code ${statusClass(value)}`}
      title={`${year}: ${statusLabel(value)}`}
      aria-label={`${year}: ${statusLabel(value)}`}
    >
      {statusCode(value)}
    </span>
  );
}

function Ratings({ occupation, mobile = false }) {
  const byYear = new Map(
    (occupation.years || []).map((item) => [item.year, item]),
  );
  const thisYear = byYear.get(2026);

  if (mobile) {
    return (
      <div className="card-ratings">
        <div className="current-rating">
          <span className="rating-label">2026 rating</span>
          <Rating year={2026} value={thisYear?.shortageStatus} />
        </div>
        <div className="past-ratings">
          {YEARS.slice(1).map((year) => (
            <div className="year-rating" key={year}>
              <span>{year}</span>
              <Rating year={year} value={byYear.get(year)?.shortageStatus} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <td className="rating-cell">
        <Rating year={2026} value={thisYear?.shortageStatus} />
      </td>
      {YEARS.slice(1).map((year) => (
        <td className="rating-cell" key={year}>
          <Rating year={year} value={byYear.get(year)?.shortageStatus} />
        </td>
      ))}
    </>
  );
}

export default function Content() {
  const occupations = useSelector((state) => state.data.data);
  const loading = useSelector((state) => state.data.loading);
  const [query, setQuery] = useState('');
  const [selectedOccupation, setSelectedOccupation] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia('(max-width: 600px)').matches,
  );
  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 600px)');
    const updateViewport = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const options = useMemo(
    () =>
      occupations.map((item) => ({
        value: item.oscaCode,
        label: item.occupation,
        searchText: item.occupation,
      })),
    [occupations],
  );

  const filtered = useMemo(() => {
    const terms = normalise(query).split(/\s+/).filter(Boolean);
    return occupations
      .filter((item) => {
        if (selectedOccupation && item.oscaCode !== selectedOccupation.value)
          return false;
        if (!terms.length) return true;
        const searchable = normalise(item.occupation);
        return terms.every((term) => searchable.includes(term));
      })
      .sort((a, b) => {
        const result = String(a.occupation).localeCompare(
          String(b.occupation),
          undefined,
          { sensitivity: 'base' },
        );
        return sortDirection === 'asc' ? result : -result;
      });
  }, [occupations, query, selectedOccupation, sortDirection]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const start = filtered.length ? (currentPage - 1) * pageSize + 1 : 0;
  const end = Math.min(currentPage * pageSize, filtered.length);

  const changeQuery = (value, action) => {
    if (action.action === 'input-change') {
      setQuery(value);
      setSelectedOccupation(null);
      setPage(1);
    }
  };

  const chooseOccupation = (option) => {
    setSelectedOccupation(option || null);
    setQuery('');
    setPage(1);
  };

  const changeSort = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setPage(1);
  };

  return (
    <ContentStyles>
      <section
        className="occupation-tool"
        aria-label="Occupation shortage ratings"
      >
        <div className="search-wrap">
          <Select
            inputId="occupation-search"
            classNamePrefix="occupation-select"
            options={options}
            value={selectedOccupation}
            inputValue={query}
            onInputChange={changeQuery}
            onChange={chooseOccupation}
            placeholder="Search by occupation"
            isClearable
            isSearchable
            noOptionsMessage={() => 'No occupations found'}
            filterOption={(option, input) => {
              const terms = normalise(input).split(/\s+/).filter(Boolean);
              const searchable = normalise(option.data.searchText);
              return terms.every((term) => searchable.includes(term));
            }}
            aria-label="Search by occupation name"
          />
        </div>

        <div className="table-tools">
          <p className="results-count" aria-live="polite">
            {loading && occupations.length === 0
              ? 'Loading occupations…'
              : `Showing ${start}–${end} of ${filtered.length} occupations`}
          </p>
        </div>

        <div
          className="table-scroll"
          role="region"
          aria-label="Occupation ratings results"
        >
          <table className="desktop-table">
            <thead>
              <tr>
                <th
                  scope="col"
                  aria-sort={
                    sortDirection === 'asc' ? 'ascending' : 'descending'
                  }
                >
                  <button
                    type="button"
                    onClick={changeSort}
                    aria-label={`Sort occupation ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    Occupation {sortDirection === 'asc' ? '↑' : '↓'}
                  </button>
                </th>
                <th scope="col" className="rating-heading">
                  2026 rating
                </th>
                {YEARS.slice(1).map((year) => (
                  <th scope="col" className="rating-heading" key={year}>
                    {year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((occupation) => (
                <tr key={occupation.oscaCode}>
                  <td>
                    <span className="occupation-name">
                      {occupation.occupation}
                    </span>
                    <span className="osca-code">{occupation.oscaCode}</span>
                  </td>
                  <Ratings occupation={occupation} />
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mobile-cards">
            {pageRows.map((occupation) => (
              <article className="occupation-card" key={occupation.oscaCode}>
                <header>
                  <h2>{occupation.occupation}</h2>
                  <span className="osca-code">{occupation.oscaCode}</span>
                </header>
                <Ratings occupation={occupation} mobile />
              </article>
            ))}
          </div>

          {!loading && pageRows.length === 0 && (
            <p className="empty-state">No occupations match your search.</p>
          )}
        </div>

        <nav className="pagination" aria-label="Results pages">
          <button
            type="button"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {pageCount}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(pageCount, currentPage + 1))}
            disabled={currentPage >= pageCount}
          >
            Next
          </button>
        </nav>
      </section>
    </ContentStyles>
  );
}
